import React, { useMemo } from 'react';
import { DynamicTableStateless } from '@atlaskit/dynamic-table';
import Heading from '@atlaskit/heading';
import { Box, Text } from '@atlaskit/primitives';
import { Card, Provider as SmartCardProvider, CardAction } from '@atlaskit/smart-card';
import PageHeader from '@atlassian/jira-common-components-page-header';
import {
	GenericPermissionErrorPageAsync,
	JSErrorPageAsync,
} from '@atlassian/jira-error-pages/src/async.tsx';
import { DocumentTitle } from '@atlassian/jira-global-document-title';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { productsJswStorageManagementResource } from '@atlassian/jira-router-resources-products-jsw-storage-management';
import { PageLayout } from '@atlassian/jira-spa-apps-page-layout/src/page/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { prettyBytes } from './common/utils/pretty-bytes';
import { messages } from './messages';
import { StorageManagementContextualAnalyticsData } from './ui/storage-management-contextual-analytics-data';

// There is no link yet, but there might be soon.
// https://hello.atlassian.net/browse/KURO-4296
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const link = (children: any) => <>{children}</>;

export const ProductsJswStorageManagement = () => {
	const { data, error, loading: isLoading } = useResource(productsJswStorageManagementResource);
	const tenantContext = useTenantContext();
	const { formatMessage } = useIntl();

	const rows = useMemo(
		() =>
			data?.issues.map((issue, index) => ({
				key: `row-${index}-${issue.key}`,
				cells: [
					{
						key: issue.key,
						content: (
							<Box paddingBlock="space.050">
								<Card
									appearance="inline"
									// todo: Use real issue url
									url={`https://${window.location.host}/browse/${issue.key}`}
									actionOptions={{
										hide: false,
										exclude: [CardAction.ChangeStatusAction],
									}}
								/>
							</Box>
						),
					},
					{
						key: issue.size,
						content: prettyBytes(issue.size),
					},
				],
			})) ?? [],
		[data],
	);

	if (!tenantContext.isAdmin) {
		return <GenericPermissionErrorPageAsync />;
	}

	if (error) {
		return <JSErrorPageAsync />;
	}

	return (
		<StorageManagementContextualAnalyticsData>
			<DocumentTitle title={formatMessage(messages.documentTitle)} />
			<PageLayout>
				<PageHeader>Storage management</PageHeader>
				<Box paddingBlockEnd="space.200">
					<Text as="p">
						<FormattedMessage {...messages.description} values={{ link }} />
					</Text>
				</Box>
				<SmartCardProvider>
					<DynamicTableStateless
						head={{
							cells: [
								{
									key: 'key',
									content: formatMessage(messages.tableHeaderIssue),
								},
								{
									key: 'size',
									content: formatMessage(messages.tableHeaderTotalAttachmentSize),
								},
							],
						}}
						rows={rows}
						loadingSpinnerSize="large"
						isLoading={isLoading}
						emptyView={<Heading size="large">{formatMessage(messages.tableEmpty)}</Heading>}
					/>
				</SmartCardProvider>
			</PageLayout>
		</StorageManagementContextualAnalyticsData>
	);
};
